type DraggableOptions = {
  handle?: HTMLElement;
  boundary?: HTMLElement | "parent" | "body";
  onDragStart?: (el: HTMLElement) => void;
  onDrag?: (el: HTMLElement, x: number, y: number) => void;
  onDragEnd?: (el: HTMLElement) => void;
};

export class Draggable {
  private readonly element: HTMLElement;
  private handle: HTMLElement;
  private readonly boundaryEl: HTMLElement;
  private options: DraggableOptions;

  private isDragging = false;
  private startClientX = 0;
  private startClientY = 0;
  private startX = 0;
  private startY = 0;
  private currentX = 0;
  private currentY = 0;

  private minY = 0;
  private maxY = 0;
  private minX = 0;
  private maxX = 0;

  private document = document;
  private body = this.document.body;
  private doc = this.document.documentElement;

  constructor(element: HTMLElement, options: DraggableOptions = {}) {
    this.element = element;
    this.options = options;
    this.handle = options.handle ?? element;

    // 边界元素
    if (options.boundary === "body") {
      this.boundaryEl = this.doc;
    } else if (options.boundary instanceof HTMLElement) {
      this.boundaryEl = options.boundary;
    } else {
      this.boundaryEl = element.parentElement ?? this.doc;
    }
    if (this.boundaryEl === this.body) {
      this.boundaryEl = this.doc;
    }

    this.handle.style.cursor = "move";

    // 初始化 transform
    this.element.style.transform = "translate(0px, 0px)";

    this.initMinAndMax();
    this.handle.addEventListener("mousedown", this.onMouseDown);
    this.handle.addEventListener("touchstart", this.onTouchStart, {
      passive: false,
    });
  }

  initMinAndMax(): void {
    const boundaryRect = this.boundaryEl.getBoundingClientRect();
    const elRect = this.element.getBoundingClientRect();

    if (
      this.boundaryEl === this.doc &&
      boundaryRect.height < this.doc.clientHeight
    ) {
      boundaryRect.height = this.doc.clientHeight;
    }

    // 统一计算相对位置
    const relativeX = elRect.left - boundaryRect.left;
    const relativeY = elRect.top - boundaryRect.top;

    this.minX = -relativeX; // 向左最多能移动到边界起点
    this.minY = -relativeY; // 向上最多能移动到边界起点
    this.maxX = boundaryRect.width - elRect.width - relativeX; // 向右最多能移动到边界终点
    this.maxY = boundaryRect.height - elRect.height - relativeY; // 向下最多能移动到边界终点
  }

  // ------------------- PC 事件 -------------------
  private onMouseDown = (e: MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    this.startDrag(e.clientX, e.clientY);
    this.document.addEventListener("mousemove", this.onMouseMove);
    this.document.addEventListener("mouseup", this.onMouseUp);
  };

  private onMouseMove = (e: MouseEvent): void => {
    this.continueDrag(e.clientX, e.clientY);
  };

  private onMouseUp = (e: MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    this.endDrag();
    this.document.removeEventListener("mousemove", this.onMouseMove);
    this.document.removeEventListener("mouseup", this.onMouseUp);
  };

  // ------------------- 移动端事件 -------------------
  private onTouchStart = (e: TouchEvent): void => {
    if (e.touches.length > 1) {
      return;
    }
    const touch = e.touches[0];
    this.startDrag(touch.clientX, touch.clientY);
    this.document.addEventListener("touchmove", this.onTouchMove, {
      passive: false,
    });
    this.document.addEventListener("touchend", this.onTouchEnd);
  };

  private onTouchMove = (e: TouchEvent): void => {
    if (e.touches.length > 1) {
      return;
    }
    const touch = e.touches[0];
    this.continueDrag(touch.clientX, touch.clientY);
  };

  private onTouchEnd = (): void => {
    this.endDrag();
    this.document.removeEventListener("touchmove", this.onTouchMove);
    this.document.removeEventListener("touchend", this.onTouchEnd);
  };

  // ------------------- 拖拽逻辑 -------------------
  private startDrag(clientX: number, clientY: number): void {
    this.isDragging = true;

    // 记录起始点
    this.startClientX = clientX;
    this.startClientY = clientY;

    // 拖拽开始时的位移
    this.startX = this.currentX;
    this.startY = this.currentY;

    this.options.onDragStart?.(this.element);
  }

  private continueDrag(clientX: number, clientY: number): void {
    if (!this.isDragging) {
      return;
    }

    const dx = clientX - this.startClientX;
    const dy = clientY - this.startClientY;

    let nextX = this.startX + dx;
    let nextY = this.startY + dy;

    // 应用边界约束
    nextX = Math.max(this.minX, Math.min(nextX, this.maxX));
    nextY = Math.max(this.minY, Math.min(nextY, this.maxY));

    this.currentX = nextX;
    this.currentY = nextY;

    this.element.style.transform = `translate(${nextX}px, ${nextY}px)`;
    this.options.onDrag?.(this.element, nextX, nextY);
  }

  private endDrag(): void {
    if (!this.isDragging) {
      return;
    }
    this.isDragging = false;
    this.options.onDragEnd?.(this.element);
  }

  public destroy(): void {
    this.handle.removeEventListener("mousedown", this.onMouseDown);
    this.handle.removeEventListener("touchstart", this.onTouchStart);
  }
}
